import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export default function MagicLinkConsume() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const [params] = useSearchParams();
    const [status, setStatus] = useState('loading');
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        let redirectTimer;
        const token = params.get('t');
        if (!token) {
            setStatus('error');
            setError('No token in URL');
            return;
        }
        (async () => {
            try {
                const r = await api.post('/auth/magic-link/consume', { token });
                if (r.data?.session_token) {
                    localStorage.setItem('session_token', r.data.session_token);
                }
                if (!active) return;
                setUser(r.data.user);
                setStatus('success');
                redirectTimer = setTimeout(() => navigate('/dashboard', { replace: true }), 1200);
            } catch (err) {
                if (!active) return;
                setStatus('error');
                setError(err?.response?.data?.detail || 'Link invalid or expired');
            }
        })();
        return () => { active = false; if (redirectTimer) clearTimeout(redirectTimer); };
    }, [navigate, params, setUser]);

    return (
        <div className="min-h-screen flex items-center justify-center px-5" data-testid="magic-consume">
            <div className="bg-brand-card border border-brand-border rounded-3xl p-8 max-w-sm w-full text-center">
                {status === 'loading' && (
                    <>
                        <Loader2 className="mx-auto animate-spin text-brand-green" size={32} />
                        <p className="mt-4 text-brand-ink">Signing you in…</p>
                    </>
                )}
                {status === 'success' && (
                    <>
                        <CheckCircle className="mx-auto text-brand-green" size={32} />
                        <p className="mt-4 text-brand-ink font-medium">Welcome back!</p>
                        <p className="text-xs text-brand-ink3 mt-1">Redirecting…</p>
                    </>
                )}
                {status === 'error' && (
                    <>
                        <AlertCircle className="mx-auto text-brand-terracotta" size={32} />
                        <p className="mt-4 text-brand-ink font-medium">Something went wrong</p>
                        <p className="text-xs text-brand-ink3 mt-1" data-testid="magic-error">{error}</p>
                        <button
                            onClick={() => navigate('/login')}
                            data-testid="btn-back-to-login"
                            className="mt-4 w-full bg-brand-green hover:bg-brand-greenDark text-white py-2.5 rounded-full text-sm"
                        >
                            Back to login
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

export function VerifyEmailConsume() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('loading');

    useEffect(() => {
        let active = true;
        let redirectTimer;
        const token = params.get('t');
        if (!token) { setStatus('error'); return; }
        (async () => {
            try {
                await api.post('/auth/verify-email/confirm', { token });
                if (!active) return;
                setStatus('success');
                redirectTimer = setTimeout(() => navigate('/profile', { replace: true }), 1500);
            } catch {
                if (!active) return;
                setStatus('error');
            }
        })();
        return () => { active = false; if (redirectTimer) clearTimeout(redirectTimer); };
    }, [navigate, params]);

    return (
        <div className="min-h-screen flex items-center justify-center px-5" data-testid="verify-consume">
            <div className="bg-brand-card border border-brand-border rounded-3xl p-8 max-w-sm w-full text-center">
                {status === 'loading' && <><Loader2 className="mx-auto animate-spin text-brand-green" size={32} /><p className="mt-4">Verifying…</p></>}
                {status === 'success' && <><CheckCircle className="mx-auto text-brand-green" size={32} /><p className="mt-4 font-medium">Email verified! 🎉</p></>}
                {status === 'error' && <><AlertCircle className="mx-auto text-brand-terracotta" size={32} /><p className="mt-4">Link invalid or expired</p></>}
            </div>
        </div>
    );
}
